import FormInputError from "components/General/FormInputError";
import { v4 as uuidv4 } from "uuid";
import { states } from "js/placeholderData/states";
import { Controller } from "react-hook-form";
import { useState } from "react";
import MaskedPhoneNumberInput from "components/General/Form/MaskedPhoneNumberInput";
import SSNInput from "components/General/Form/SSNInput";
import { useTranslation } from "react-i18next";
import RELEASING_AutoCompleteComboBox from "components/General/AutoCompleteComboBox/RELEASING_AutoCompleteComboBox";

export default function BeneficiaryInfoCard({
  componentIndex,
  lastComponent,
  FACStepThreeRegister,
  append,
  remove,
  errors,
  setValue,
  control,
  clearErrors,
  setError,
  watch,
}) {
  const uuid = uuidv4();
  const { t } = useTranslation();
  const initialSelectedState = watch(`beneficiaries[${componentIndex}].state`);
  const initialRelationship = watch(
    `beneficiaries[${componentIndex}].relationship`
  );
  const [selectedState, setSelectedState] = useState(
    initialSelectedState || { value: "all" }
  );
  const [beneficiaryRelationship, setBeneficiaryRelationship] = useState(
    initialRelationship || { value: "all" }
  );

  return (
    <div className="mt-6 grid gap-4 grid-cols-1 lg:gap-6">
      <div className="grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-[2fr,2fr,1fr]">
        {componentIndex > 0 && (
          <div className="md:col-span-3">
            <button
              className="ui-btn-primary-error my-2"
              type="button"
              onClick={() => remove(componentIndex)}
            >
              {t("UnauthFACBeneficiary-btn")}
            </button>
          </div>
        )}
        <div>
          <label htmlFor={`${uuid}-fn-beneficiary`} className="block mb-1">
            {t("UnauthFACBeneficiary-fn")}
          </label>
          <input
            id={`${uuid}-fn-beneficiary`}
            type="text"
            placeholder={t("UnauthFACBeneficiary-fn-placeholder")}
            {...FACStepThreeRegister(
              `beneficiaries[${componentIndex}].firstName`,
              {
                required: `Please enter a first name for beneficiary - ${
                  componentIndex + 1
                }`,
              }
            )}
          />
          <FormInputError
            id={`${uuid}-fn-beneficiary-error`}
            errorMessage={
              errors?.beneficiaries?.length
                ? errors?.beneficiaries[componentIndex]?.firstName?.message
                : ""
            }
          />
        </div>

        <div>
          <label htmlFor={`${uuid}-ln-beneficiary`} className="block mb-1">
            {t("UnauthFACBeneficiary-ln")}
          </label>
          <input
            id={`${uuid}-ln-beneficiary`}
            type="text"
            placeholder={t("UnauthFACBeneficiary-ln-placeholder")}
            {...FACStepThreeRegister(
              `beneficiaries[${componentIndex}].lastName`,
              {
                required: `Please enter a last name for beneficiary - ${
                  componentIndex + 1
                }`,
              }
            )}
          />
          <FormInputError
            id={`${uuid}-ln-beneficiary-error`}
            errorMessage={
              errors?.beneficiaries?.length
                ? errors?.beneficiaries[componentIndex]?.lastName?.message
                : ""
            }
          />
        </div>

        <div>
          <label htmlFor={`${uuid}-mn-beneficiary`} className="block mb-1">
            {t("UnauthFACBeneficiary-mi")}
          </label>
          <input
            id={`${uuid}-mn-beneficiary`}
            type="text"
            {...FACStepThreeRegister(
              `beneficiaries[${componentIndex}].middleName`
            )}
          />
          <FormInputError
            id={`${uuid}-mn-beneficiary-error`}
            errorMessage={
              errors?.beneficiaries?.length
                ? errors?.beneficiaries[componentIndex]?.middleName?.message
                : ""
            }
          />
        </div>
      </div>

      <div>
        <label htmlFor={`${uuid}-sa-beneficiary`} className="block mb-1">
          {t("UnauthFACBeneficiary-mailling")}
        </label>
        <input
          id={`${uuid}-sa-beneficiary`}
          type="text"
          placeholder={t("UnauthFACBeneficiary-address-placeholder")}
          {...FACStepThreeRegister(
            `beneficiaries[${componentIndex}].streetAddress`
          )}
        />
        <FormInputError
          id={`${uuid}-sa-beneficiary-error`}
          errorMessage={
            errors?.beneficiaries?.length
              ? errors?.beneficiaries[componentIndex]?.streetAddress?.message
              : ""
          }
        />
      </div>

      <div>
        <label htmlFor={`${uuid}-sa2-beneficiary`} className="block mb-1">
          {t("UnauthFACBeneficiary-mailling-2")}
        </label>
        <input
          id={`${uuid}-sa2-beneficiary`}
          type="text"
          placeholder={t("UnauthFACBeneficiary-address-line2-placeholder")}
          {...FACStepThreeRegister(
            `beneficiaries[${componentIndex}].streetAddress2`
          )}
        />
        <FormInputError
          id={`${uuid}-sa2-beneficiary-error`}
          errorMessage={
            errors?.beneficiaries?.length
              ? errors?.beneficiaries[componentIndex]?.streetAddress2?.message
              : ""
          }
        />
      </div>
      <div className="grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-[1.5fr,1fr,1fr]">
        <div>
          <label htmlFor={`${uuid}-city-beneficiary`} className="block mb-1">
            {t("UnauthFACBeneficiary-city")}
          </label>
          <input
            id={`${uuid}-city-beneficiary`}
            type="text"
            placeholder={t("UnauthFACBeneficiary-city-placeholder")}
            {...FACStepThreeRegister(`beneficiaries[${componentIndex}].city`)}
          />
          <FormInputError
            id={`${uuid}-city-beneficiary-error`}
            errorMessage={
              errors?.beneficiaries?.length
                ? errors?.beneficiaries[componentIndex]?.city?.message
                : ""
            }
          />
        </div>

        <div>
          <Controller
            name={`beneficiaries[${componentIndex}].state`}
            control={control}
            render={() => (
              <RELEASING_AutoCompleteComboBox
                labelName={t("UnauthFACBeneficiary-state")}
                inputPlaceholderText={t(
                  "UnauthFACBeneficiary-state-placeholder"
                )}
                options={[
                  {
                    value: "all",
                    displayAs: t("UnauthFACBeneficiary-state"),
                    selectedPolicyDisplayAs: t("UnauthFACBeneficiary-state"),
                    isDisplayOnly: true,
                  },
                  ...states.map((state) => ({
                    value: state.abbreviation,
                    displayAs: state.name,
                    selectedPolicyDisplayAs: state.name,
                    isDisplayOnly: true,
                  })),
                ]}
                selectedOption={selectedState}
                setSelectedOption={(option) => {
                  setSelectedState(option);
                  if (option?.value !== "all") {
                    setValue(
                      `beneficiaries[${componentIndex}].state`,
                      option.value
                    );
                  } else {
                    setValue(`beneficiaries[${componentIndex}].state`, "");
                  }
                }}
              />
            )}
          />

          <FormInputError
            id={`${uuid}-state-beneficiary-error`}
            errorMessage={
              errors?.beneficiaries?.length
                ? errors?.beneficiaries[componentIndex]?.state?.message
                : ""
            }
          />
        </div>

        <div>
          <label htmlFor={`${uuid}-zip-beneficiary`} className="block mb-1">
            {t("UnauthFACBeneficiary-zip")}
          </label>
          <input
            id={`${uuid}-zip-beneficiary`}
            type="text"
            placeholder={t("UnauthFACBeneficiary-zip-placeholder")}
            {...FACStepThreeRegister(`beneficiaries[${componentIndex}].zip`, {
              pattern: {
                value: /^\d{5}$/,
                message: "Please enter a valid zipcode (5 digits)",
              },
              maxLength: {
                value: 5,
                message: "Please enter only 5 digits for zipcode",
              },
            })}
          />
          <FormInputError
            id={`${uuid}-zip-beneficiary-error`}
            errorMessage={
              errors?.beneficiaries?.length
                ? errors?.beneficiaries[componentIndex]?.zip?.message
                : ""
            }
          />
        </div>
      </div>

      <div className="grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-[1fr,1fr,1fr]">
        <div>
          <label htmlFor={`${uuid}-phone-beneficiary`} className="block mb-1">
            {t("UnauthFACBeneficiary-ph")}
          </label>
          <MaskedPhoneNumberInput
            name="phone"
            describedby={`${uuid}-phone-beneficiary-note`}
            register={FACStepThreeRegister}
            {...FACStepThreeRegister(`beneficiaries[${componentIndex}].phone`)}
            setValue={setValue}
            required={false}
          />
          <FormInputError
            id={`${uuid}-phone-beneficiary-error`}
            errorMessage={
              errors?.beneficiaries?.length
                ? errors?.beneficiaries[componentIndex]?.phone?.message
                : ""
            }
          />
        </div>

        <div>
          <label htmlFor={`${uuid}-email-beneficiary`} className="block mb-1">
            {t("UnauthFACBeneficiary-email")}
          </label>
          <input
            id={`${uuid}-email-beneficiary`}
            type="text"
            placeholder={t("UnauthFACBeneficiary-email-placeholder")}
            {...FACStepThreeRegister(`beneficiaries[${componentIndex}].email`, {
              pattern: {
                value: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
                message: "Please enter a valid email",
              },
            })}
          />
          <FormInputError
            id={`${uuid}-email-beneficiary-error`}
            errorMessage={
              errors?.beneficiaries?.length
                ? errors?.beneficiaries[componentIndex]?.email?.message
                : ""
            }
          />
        </div>

        <div>
          <label htmlFor={`${uuid}-dob-beneficiary`} className="block mb-1">
            {t("UnauthFACBeneficiary-dob")}
          </label>
          <input
            id={`${uuid}-dob-beneficiary`}
            type="date"
            {...FACStepThreeRegister(`beneficiaries[${componentIndex}].dob`)}
          />
          <FormInputError
            id={`${uuid}-email-dob-error`}
            errorMessage={
              errors?.beneficiaries?.length
                ? errors?.beneficiaries[componentIndex]?.dob?.message
                : ""
            }
          />
        </div>
      </div>
      {/* LAST ROW ON BENEFICIARY STEP */}
      <div className="grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-[1fr,2fr]">
        <div className="relative">
          <label htmlFor={`${uuid}-ssn-beneficiary`} className="block mb-1">
            {t("UnauthFACBeneficiary-ssn")}
          </label>
          <SSNInput
            name="ssn"
            describedby={`${uuid}-ssn-beneficiary`}
            register={FACStepThreeRegister}
            length="full"
            required={false}
            setValue={setValue}
            {...FACStepThreeRegister(`beneficiaries[${componentIndex}].ssn`)}
          />
          <FormInputError
            id={`${uuid}-ssn-beneficiary-error`}
            errorMessage={
              errors?.beneficiaries?.length
                ? errors?.beneficiaries[componentIndex]?.ssn?.message
                : ""
            }
          />
        </div>

        <div>
          <Controller
            name={`beneficiaries[${componentIndex}].relationship`}
            control={control}
            render={() => (
              <RELEASING_AutoCompleteComboBox
                labelName={t("UnauthFACBeneficiary-rd")}
                inputPlaceholderText={t("UnauthFACBeneficiary-rd-optn")}
                options={[
                  {
                    value: "all",
                    displayAs: t("UnauthFACBeneficiary-rd-optn"),
                    selectedPolicyDisplayAs: t("UnauthFACBeneficiary-rd-optn"),
                    isDisplayOnly: true,
                  },
                  {
                    value: "owner",
                    displayAs: t("UnauthFACBeneficiary-rd-optn-1"),
                    selectedPolicyDisplayAs: t(
                      "UnauthFACBeneficiary-rd-optn-1"
                    ),
                    isDisplayOnly: true,
                  },
                  {
                    value: "spouse",
                    displayAs: t("UnauthFACBeneficiary-rd-optn-2"),
                    selectedPolicyDisplayAs: t(
                      "UnauthFACBeneficiary-rd-optn-2"
                    ),
                    isDisplayOnly: true,
                  },
                  {
                    value: "child",
                    displayAs: t("UnauthFACBeneficiary-rd-optn-3"),
                    selectedPolicyDisplayAs: t(
                      "UnauthFACBeneficiary-rd-optn-3"
                    ),
                    isDisplayOnly: true,
                  },
                  {
                    value: "dependent",
                    displayAs: t("UnauthFACBeneficiary-rd-optn-4"),
                    selectedPolicyDisplayAs: t(
                      "UnauthFACBeneficiary-rd-optn-4"
                    ),
                    isDisplayOnly: true,
                  },
                  {
                    value: "other",
                    displayAs: t("UnauthFACBeneficiary-rd-optn-5"),
                    selectedPolicyDisplayAs: t(
                      "UnauthFACBeneficiary-rd-optn-5"
                    ),
                    isDisplayOnly: true,
                  },
                ]}
                selectedOption={beneficiaryRelationship}
                setSelectedOption={(option) => {
                  setBeneficiaryRelationship(option);
                  if (option?.value !== "all") {
                    clearErrors(
                      `beneficiaries[${componentIndex}].relationship`
                    );
                    setValue(
                      `beneficiaries[${componentIndex}].relationship`,
                      option.value
                    );
                  } else {
                    setError(`beneficiaries[${componentIndex}].relationship`, {
                      message: `Please select beneficiary's relationship to the deceased - ${
                        componentIndex + 1
                      }`,
                    });
                    setValue(
                      `beneficiaries[${componentIndex}].relationship`,
                      ""
                    );
                  }
                }}
              />
            )}
            rules={{
              required: `Please select beneficiary's relationship to the deceased - ${
                componentIndex + 1
              }`,
            }}
          />

          <FormInputError
            id={`${uuid}-relationship-beneficiary`}
            errorMessage={
              errors?.beneficiaries?.length
                ? errors?.beneficiaries[componentIndex]?.relationship?.message
                : ""
            }
          />
        </div>
      </div>

      {lastComponent && (
        <div className="flex justify-start">
          <button
            className="ui-btn-secondary mt-4"
            type="button"
            onClick={() => append({ firstName: "" })}
          >
            {t("UnauthFACBeneficiary-btn-1")}
          </button>
        </div>
      )}
    </div>
  );
}
